
import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import Swal from 'sweetalert2';

type AuthMode = 'signIn' | 'signUp' | 'forgotPassword' | 'updatePassword';

const Auth: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [mode, setMode] = useState<AuthMode>('signIn');

  useEffect(() => {
    // Check if we just landed from a recovery link
    const handleRecovery = async () => {
      const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event) => {
        if (event === 'PASSWORD_RECOVERY') {
          setMode('updatePassword');
        }
      });
      return () => subscription.unsubscribe();
    };
    handleRecovery();
  }, []);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === 'signUp') {
        const { error } = await supabase.auth.signUp({ 
          email, 
          password,
          options: {
            data: {
              full_name: email.split('@')[0],
              is_signup: true 
            }
          }
        });
        if (error) throw error;
        Swal.fire({
          title: 'ลงทะเบียนสำเร็จ!',
          text: 'โปรดตรวจสอบอีเมลของคุณเพื่อยืนยันตน',
          icon: 'success',
          confirmButtonColor: '#2563eb'
        });
        setMode('signIn');
      } else if (mode === 'signIn') {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      } else if (mode === 'forgotPassword') {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: window.location.origin,
        });
        if (error) throw error;
        Swal.fire({
          title: 'ส่งลิงก์สำเร็จ',
          text: 'เราได้ส่งลิงก์รีเซ็ตรหัสผ่านไปยังอีเมลของคุณแล้ว',
          icon: 'info',
          confirmButtonColor: '#2563eb'
        });
        setMode('signIn');
      } else if (mode === 'updatePassword') {
        if (password !== confirmPassword) {
          throw new Error('รหัสผ่านไม่ตรงกัน');
        }
        const { error } = await supabase.auth.updateUser({ password });
        if (error) throw error;
        Swal.fire({
          title: 'อัปเดตรหัสผ่านสำเร็จ',
          text: 'คุณสามารถเข้าใช้งานระบบได้ทันที',
          icon: 'success',
          confirmButtonColor: '#2563eb'
        });
      }
    } catch (error: any) {
      Swal.fire({
        title: 'เกิดข้อผิดพลาด',
        text: error.message || 'การดำเนินการล้มเหลว',
        icon: 'error',
        confirmButtonColor: '#2563eb'
      });
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    switch (mode) {
      case 'signUp': return 'สร้างบัญชีสุขภาพ';
      case 'forgotPassword': return 'ลืมรหัสผ่าน?';
      case 'updatePassword': return 'ตั้งรหัสผ่านใหม่';
      default: return 'ยินดีต้อนรับสู่ DPAC Health';
    }
  };

  const getDescription = () => {
    switch (mode) {
      case 'forgotPassword': return 'กรอกอีเมลเพื่อรับลิงก์สำหรับรีเซ็ตรหัสผ่าน';
      case 'updatePassword': return 'โปรดกำหนดรหัสผ่านใหม่ที่มีความปลอดภัย';
      default: return 'จัดการข้อมูลและทำนายผลสุขภาพด้วย AI';
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen px-6 bg-slate-50 dark:bg-slate-950 transition-colors duration-300">
      <div className="w-full max-w-md bg-white dark:bg-slate-900 p-10 rounded-[40px] shadow-2xl border border-white dark:border-slate-800 relative overflow-hidden transition-colors">
        {/* Decorative Background */}
        <div className="absolute top-0 right-0 -mr-16 -mt-16 w-48 h-48 bg-blue-50 dark:bg-blue-900/10 rounded-full opacity-50"></div>
        <div className="absolute bottom-0 left-0 -ml-8 -mb-8 w-24 h-24 bg-indigo-50 dark:bg-indigo-900/10 rounded-full opacity-50"></div>

        <div className="relative z-10">
          <div className="mb-10 text-center">
            <div className="w-16 h-16 bg-blue-600 dark:bg-blue-500 rounded-2xl flex items-center justify-center text-white mx-auto mb-6 shadow-xl shadow-blue-200 dark:shadow-none">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </div>
            <h2 className="text-3xl font-black text-slate-800 dark:text-slate-100 leading-tight">
              {getTitle()}
            </h2>
            <p className="text-slate-400 dark:text-slate-500 mt-2 text-sm font-medium">{getDescription()}</p>
          </div>
          
          <form onSubmit={handleAuth} className="space-y-5">
            {mode !== 'updatePassword' && (
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em] ml-2">Email Address</label>
                <input 
                  type="email" 
                  required
                  className="w-full p-4 bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all text-slate-700 dark:text-slate-200 font-medium"
                  placeholder="you@dpac-health.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            )}

            {mode !== 'forgotPassword' && (
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em] ml-2">
                  {mode === 'updatePassword' ? 'New Password' : 'Password'}
                </label>
                <input 
                  type="password" 
                  required
                  minLength={6}
                  className="w-full p-4 bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all text-slate-700 dark:text-slate-200"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            )}

            {mode === 'updatePassword' && (
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em] ml-2">Confirm Password</label>
                <input 
                  type="password" 
                  required
                  minLength={6}
                  className="w-full p-4 bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all text-slate-700 dark:text-slate-200"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>
            )}

            {mode === 'signIn' && (
              <div className="text-right">
                <button 
                  type="button" 
                  onClick={() => setMode('forgotPassword')}
                  className="text-xs font-bold text-blue-600 dark:text-blue-400 hover:text-blue-700 transition-colors"
                >
                  ลืมรหัสผ่าน?
                </button>
              </div>
            )}

            <button 
              disabled={loading}
              className="w-full py-5 bg-blue-600 dark:bg-blue-500 text-white font-black rounded-2xl shadow-xl shadow-blue-100 dark:shadow-none hover:bg-blue-700 dark:hover:bg-blue-600 active:scale-[0.98] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              ) : (
                <span>
                  {mode === 'signIn' && 'เข้าสู่ระบบ'}
                  {mode === 'signUp' && 'ลงทะเบียนใช้งาน'}
                  {mode === 'forgotPassword' && 'ส่งลิงก์รีเซ็ต'}
                  {mode === 'updatePassword' && 'ยืนยันรหัสผ่านใหม่'}
                </span>
              )}
            </button>
          </form>

          <div className="mt-10 text-center border-t border-slate-50 dark:border-slate-800 pt-8 flex flex-col gap-3">
            {mode === 'signIn' ? (
              <button 
                onClick={() => setMode('signUp')}
                className="text-sm font-bold text-slate-400 dark:text-slate-500 hover:text-blue-600 transition-colors"
              >
                ยังไม่เคยใช้งานระบบ? <span className="text-blue-600 dark:text-blue-400">สมัครสมาชิกฟรี</span>
              </button>
            ) : mode !== 'updatePassword' ? (
              <button 
                onClick={() => setMode('signIn')}
                className="text-sm font-bold text-slate-400 dark:text-slate-500 hover:text-blue-600 transition-colors"
              >
                กลับหน้า <span className="text-blue-600 dark:text-blue-400">เข้าสู่ระบบ</span>
              </button>
            ) : null}
          </div>
        </div>
      </div>
      
      {/* Footer Branding */}
      <p className="mt-8 text-[10px] font-bold text-slate-300 dark:text-slate-700 uppercase tracking-widest">
        DPAC Digital Health Booklet &copy; 2025
      </p>
    </div>
  );
};

export default Auth;
